import { useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
// #kinship-quiz-cutover-route — /play now renders RelationQuizV2.
// PlayPage stays as the wrapper so the ?source=mobile-app chrome
// behavior (logo + signup link hiding) is preserved for the mobile
// auth flow's WebBrowser.openBrowserAsync deep link.
import { RelationQuizV2 } from '../components/RelationQuizV2'

// #play-route-web: standalone /play surface for the Kinship Challenge
// puzzle. Same puzzle component that's embedded in the /login landing
// (RelationQuizSection wraps RelationQuiz), now reachable via a direct
// URL with no auth form chrome — easier for shareable links, WebViews
// (the mobile auth screens link here via expo-web-browser per
// #play-route-mobile-link), and SEO. No auth required.
//
// Pre-login route → eager-loaded in the router alongside Login/Signup
// so the entry chunk paints fast and stays free of the
// packages/relation-config engine (which is server-side anyway per
// #kinship-telugu-refine — the quiz fetches labels via
// POST /api/public/kinship-labels).
//
// #play-mobile-link-fixes: when opened from the mobile app's in-app
// browser, the URL carries ?source=mobile-app. In that mode the page
// hides every CTA that would route the user into the web auth flow
// (signup link, login link, brand-logo navigation) — they're already
// a mobile-app user; signing up belongs in the app, not the web.
// Direct web visitors with no `source` query param still see the
// full marketing chrome.

export function PlayPage() {
  const [searchParams] = useSearchParams()
  const isMobileApp = searchParams.get('source') === 'mobile-app'

  // Document title — English for SEO + link-preview consistency. The
  // in-puzzle Telugu/English toggle (loadLang in lib/kinshipLang.ts)
  // doesn't propagate here because the lang state is local to
  // RelationQuiz; title stays a stable English string.
  useEffect(() => {
    const prev = document.title
    document.title = 'Kinship Challenge — Jnati'
    return () => { document.title = prev }
  }, [])

  return (
    <div className="min-h-dvh bg-navy text-white">
      {/* Brand header — logo-wide PNG matching the rest of the auth
          surface. Direct web visit: logo links to /login + sign-up
          link. Mobile-app context: logo is non-clickable, sign-up
          link suppressed (in-app browser users sign up in the app). */}
      <header className="px-4 sm:px-6 py-4 flex items-center justify-between border-b border-white/10">
        {isMobileApp ? (
          <img src="/logo-wide.png" alt="Jnati" className="h-10 sm:h-12 w-auto select-none" />
        ) : (
          <Link to="/login" aria-label="Jnati home">
            <img src="/logo-wide.png" alt="Jnati" className="h-10 sm:h-12 w-auto select-none" />
          </Link>
        )}
        {!isMobileApp && (
          <Link
            to="/signup"
            className="text-gold text-sm font-bold hover:underline"
          >
            Sign up →
          </Link>
        )}
      </header>

      {/* #kinship-quiz-cutover-route — the puzzle body is now
          RelationQuizV2. V2 brings its own #FAFAF5 background so
          there's a band of cream between PlayPage's navy header
          and footer; intentional, visually separates the quiz
          from the page-level marketing surface. */}
      <RelationQuizV2 />

      {/* Footer — direct web visit gets the signup CTA; mobile-app
          context gets a quiet hint instead because routing them to
          the web /signup would lose them inside the in-app browser. */}
      {isMobileApp ? (
        <section className="px-4 pb-16 text-center">
          <p className="text-white/50 text-xs sm:text-sm max-w-md mx-auto">
            Done playing? Close this window to return to the app.
          </p>
        </section>
      ) : (
        <section className="px-4 pb-16 text-center">
          <p className="text-white/80 text-sm sm:text-base max-w-md mx-auto">
            Enjoying it? Sign up to track your progress, play with your real
            family, and see the engine work over your own tree.
          </p>
          <Link
            to="/signup?ref=play"
            className="inline-block mt-4 bg-gold text-navy font-bold px-6 py-3 rounded-lg hover:bg-gold/90 transition"
          >
            Create your account →
          </Link>
          <div className="mt-3 text-xs text-white/50">
            Already a member?{' '}
            <Link to="/login" className="text-gold hover:underline">Login</Link>
          </div>
        </section>
      )}
    </div>
  )
}
